/**
 * @file: FacadeSitesTip.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InspectionSiteInfo } from '@euler/model';
import { memo } from 'react';
import { View } from 'react-native';

export const kFacadeSitesTipLineHeight = 18;
const kRequiredSitesNoteLineHeight = 15;

// a soft amber chip - the shared `StatusColors.Warn` is too light to read at
// this size against a tinted background.
const kRequiredSitesNoteBackground = '#fff3e0';
const kRequiredSitesNoteColor = '#b15c00';

/**
 * The note below the facade illustration: how to record a site, plus which
 * sites the template makes mandatory.
 *
 * The how-to line is kept to ONE line - the iPad layout sizes its media grid
 * off this view's height (see `useFacadeSitesViewHeightByContentWidth`). The
 * required-sites note wraps as far as it needs to: the whole list has to be
 * readable.
 */
export const FacadeSitesTip = memo(
  ({
    requiredSites,
    requiredSitesLabel = '必检',
  }: {
    requiredSites?: InspectionSiteInfo[];
    /** Leads the required-sites line; hosts word it for their own flow. */
    requiredSitesLabel?: string;
  }) => {
    const { rpx } = useResponsiveContext();

    return (
      <>
        <Center>
          <Typography.Label
            size={rpx(15)}
            color="#888"
            lineHeight={rpx(kFacadeSitesTipLineHeight)}
            light
          >
            点击相应蓝点进行拍照上传
          </Typography.Label>
        </Center>

        {requiredSites?.length ? (
          <View
            css={`
              align-self: center;
              /* no horizontal margin: the host already insets this view, and
                 doubling it up costs the chip a whole line of text. */
              margin-top: ${rpx(10)}px;
              padding: ${rpx(5)}px ${rpx(10)}px;
              border-radius: ${rpx(6)}px;
              background-color: ${kRequiredSitesNoteBackground};
            `}
          >
            <Typography.Label
              light
              size={rpx(12)}
              lineHeight={rpx(kRequiredSitesNoteLineHeight)}
              color={kRequiredSitesNoteColor}
              css={`
                text-align: center;
              `}
            >
              {`${requiredSitesLabel}: ${requiredSites
                .map(x => x.name)
                .join('、')}`}
            </Typography.Label>
          </View>
        ) : null}
      </>
    );
  },
);
